import { LucideIcon } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description: string;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Premium empty state — dashed border, gold spark, token-driven.
 * Generalized from the Intern OS pattern so every screen's empty state matches.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  children,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-md border border-dashed border-border px-6 py-12 text-center',
        className,
      )}
    >
      {Icon ? (
        <Icon className="mb-3 size-8 text-muted-foreground opacity-60" />
      ) : (
        <span className="mb-3 text-2xl text-[var(--color-gold)]" aria-hidden>
          ✦
        </span>
      )}
      <p className="text-[15px] font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-xs text-sm leading-relaxed text-muted-foreground">
        {description}
      </p>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

/** Consistent error state with an optional retry. */
export function ErrorState({
  title = 'Something went wrong',
  message = "We couldn't load this. Please try again.",
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        'rounded-md border border-destructive/30 bg-destructive/5 p-6 text-center',
        className,
      )}
    >
      <p className="text-sm font-semibold text-destructive">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 text-sm font-medium text-destructive underline underline-offset-2 hover:no-underline"
        >
          Try again
        </button>
      )}
    </div>
  );
}

interface LoadingStateProps {
  rows?: number;
  className?: string;
}

/** Consistent skeleton loading block. */
export function LoadingState({ rows = 3, className }: LoadingStateProps) {
  return (
    <div className={cn('space-y-3', className)} aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full rounded-md" />
      ))}
    </div>
  );
}
